import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Video, Sparkles, Wand2, Loader2, Download, ArrowLeft, Crown,
  AlertTriangle, Clock, CheckCircle2, RefreshCw, Image as ImageIcon,
  Upload, ShieldCheck, Pencil, Plus, LifeBuoy, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import FeedbackWidget from '../components/FeedbackWidget';

const ASPECTS = [
  { id: 'landscape', label: 'Landscape · 16:9 (FB / YouTube / X / web)' },
  { id: 'reel',      label: 'Portrait · 9:16 (IG Reels / TikTok / FB Stories) — Pro only' },
];

const SORA_DURATIONS = [
  { v: 4,  label: '4 sec (fastest)' },
  { v: 8,  label: '8 sec (recommended)' },
  { v: 12, label: '12 sec (Sora single-shot max)' },
  { v: 20, label: '20 sec (12s + 1 extension)' },
  { v: 30, label: '30 sec (12s + extensions)' },
  { v: 45, label: '45 sec (12s + extensions)' },
  { v: 60, label: '60 sec (12s + extensions)' },
  { v: 90, label: '90 sec (12s + extensions)' },
  { v: 132, label: '132 sec (Sora extend ceiling)' },
];

const SEEDANCE_DURATIONS = [
  { v: 4,  label: '4 sec' },
  { v: 6,  label: '6 sec' },
  { v: 8,  label: '8 sec' },
  { v: 10, label: '10 sec' },
  { v: 12, label: '12 sec' },
  { v: 15, label: '15 sec (Seedance max)' },
];

// Auto engine accepts any of the union — backend picks the best one.
const AUTO_DURATIONS = [
  { v: 4,  label: '4 sec' },
  { v: 8,  label: '8 sec' },
  { v: 12, label: '12 sec' },
  { v: 15, label: '15 sec' },
  { v: 20, label: '20 sec (long-form)' },
  { v: 30, label: '30 sec (long-form)' },
  { v: 45, label: '45 sec (long-form)' },
  { v: 60, label: '60 sec (long-form)' },
  { v: 90, label: '90 sec (long-form)' },
  { v: 132, label: '132 sec (max long-form)' },
];

const EXTEND_DURATIONS = [
  { v: 4,  label: '+4 sec' },
  { v: 8,  label: '+8 sec' },
  { v: 12, label: '+12 sec' },
  { v: 16, label: '+16 sec' },
  { v: 20, label: '+20 sec' },
];

const MAX_TOTAL_SECONDS = 132;
const MAX_EXTENSIONS = 6;

const STAGE_LABELS = {
  queued:               { label: 'Queued',                                           progress: 5,   icon: Clock },
  rewriting_brief:      { label: 'Rewriting brief into a visual scene…',             progress: 15,  icon: Wand2 },
  using_approved_scene: { label: 'Using your approved scene…',                       progress: 20,  icon: ShieldCheck },
  reference_still:      { label: 'Generating reference still…',                      progress: 35,  icon: ImageIcon },
  sora_video:           { label: 'Generating video with Sora 2…',                    progress: 70,  icon: Wand2 },
  seedance_video:       { label: 'Generating video with Seedance 2.0…',              progress: 70,  icon: Wand2 },
  sora_extension:       { label: 'Generating extension clip with Sora 2…',           progress: 75,  icon: Wand2 },
  stitching:            { label: 'Stitching extension onto your video…',             progress: 85,  icon: ShieldCheck },
  branding:             { label: 'Stamping your logo onto the video…',                progress: 90,  icon: ShieldCheck },
  complete:             { label: 'Done',                                              progress: 100, icon: CheckCircle2 },
};

const AdminVideo = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    businessName: '',
    businessType: '',
    tone: 'friendly',
    brief: '',
    aspect: 'reel',
    duration: 8,
    model: 'sora-2-pro',
    withReferenceImage: true,
    logoSource: 'default',   // 'default' | 'custom' | 'none'
    logoId: null,
    customLogoName: '',
    engine: 'auto',          // 'auto' | 'sora' | 'seedance'
    seedanceTier: 'fast',    // 'fast' | 'standard'
    seedanceResolution: '720p',
    speedMode: false,        // ⚡ overrides engine/tier/res/refImg + caps duration
  });
  const [estCostGbp, setEstCostGbp] = useState(null);
  const [autoRouteHint, setAutoRouteHint] = useState(null);  // {engine, model?, seedanceTier?, reason}

  // Prompt Reviewer state — mandatory two-step flow.
  const [scene, setScene] = useState('');         // editable AI-rewritten scene
  const [reviewing, setReviewing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [videoUrl, setVideoUrl] = useState(null);
  const [refUrl, setRefUrl] = useState(null);

  // Extend state
  const [extendPrompt, setExtendPrompt] = useState('');
  const [extendDuration, setExtendDuration] = useState(4);
  const [extendJob, setExtendJob] = useState(null);  // active extension job

  // Recover panel — lists OpenAI's last 20 videos so users can rescue any
  // generation that disappeared from the queue (e.g., after a backend restart).
  const [recoverItems, setRecoverItems] = useState([]);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [recoverOpen, setRecoverOpen] = useState(false);

  const loadAssets = async (j) => {
    if (!j?.id) return;
    try {
      if (j.hasVideo && !videoUrl) {
        const r = await api.get(`/admin/video/file/${j.id}.mp4`, { responseType: 'blob' });
        setVideoUrl(URL.createObjectURL(r.data));
      }
      if (j.hasReferenceImage && !refUrl) {
        const r = await api.get(`/admin/video/reference/${j.id}.png`, { responseType: 'blob' });
        setRefUrl(URL.createObjectURL(r.data));
      }
    } catch (e) {
      toast({ title: 'Could not load asset', description: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => {
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      if (refUrl) URL.revokeObjectURL(refUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!job) return;
    if (job.hasVideo || job.hasReferenceImage) loadAssets(job);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, job?.hasVideo, job?.hasReferenceImage]);

  const refreshHistory = async () => {
    try {
      const { data } = await api.get('/admin/video/list');
      setHistory(data.jobs || []);
    } catch (e) { /* admin only */ }
  };

  const loadRecover = async () => {
    setRecoverLoading(true);
    try {
      const { data } = await api.get('/admin/video/recover');
      setRecoverItems(data.videos || []);
    } catch (e) {
      toast({ title: 'Could not list OpenAI videos', description: e?.response?.data?.detail || e.message });
    } finally {
      setRecoverLoading(false);
    }
  };

  const downloadRecovered = async (vid) => {
    try {
      const r = await api.get(`/admin/video/recover/${vid}.mp4`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `mbap-recovered-${vid.slice(0, 12)}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast({ title: 'Downloaded', description: 'Logo overlay re-applied.' });
    } catch (e) {
      toast({ title: 'Recovery failed', description: e?.response?.data?.detail || e.message });
    }
  };

  const openHistoryItem = async (j) => {
    if (!j.hasVideo) return;
    try {
      const r = await api.get(`/admin/video/file/${j.id}.mp4`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: 'Could not open video', description: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => { refreshHistory(); }, []);

  // Poll active job
  useEffect(() => {
    if (!job?.id || job?.status === 'done' || job?.status === 'failed') return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/video/status/${job.id}`);
        setJob(prev => ({ ...prev, ...data }));
        if (data.status === 'done' || data.status === 'failed') {
          clearInterval(pollRef.current);
          refreshHistory();
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  // Poll the extension job; when done, refresh parent job + reload stitched MP4.
  useEffect(() => {
    if (!extendJob?.id || extendJob?.status === 'done' || extendJob?.status === 'failed') return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/video/status/${extendJob.id}`);
        setExtendJob(prev => ({ ...prev, ...data }));
        if (data.status === 'done') {
          clearInterval(t);
          try {
            const { data: parentData } = await api.get(`/admin/video/status/${job.id}`);
            setJob(prev => ({ ...prev, ...parentData }));
            if (videoUrl) URL.revokeObjectURL(videoUrl);
            setVideoUrl(null);
            const r = await api.get(`/admin/video/file/${job.id}.mp4`, { responseType: 'blob' });
            setVideoUrl(URL.createObjectURL(r.data));
          } catch (e) { /* surface via toast next tick */ }
          setExtendPrompt('');
          toast({ title: 'Extension stitched in', description: 'Your video is now longer.' });
          refreshHistory();
        }
        if (data.status === 'failed') {
          clearInterval(t);
          toast({ title: 'Extension failed', description: data.error || 'See details in preview.' });
        }
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendJob?.id, extendJob?.status]);

  // Live cost estimator — recompute whenever the engine/duration/model/tier changes.
  useEffect(() => {
    let cancelled = false;
    // Resolve effective engine for cost preview when Auto is selected.
    const effEngine = (form.engine === 'auto' && autoRouteHint?.engine) || (form.engine === 'auto' ? 'sora' : form.engine);
    const effModel = (form.engine === 'auto' && autoRouteHint?.model) || form.model;
    const effTier = (form.engine === 'auto' && autoRouteHint?.seedanceTier) || form.seedanceTier;
    const payload = effEngine === 'seedance'
      ? { engine: 'seedance', duration: form.duration, seedanceTier: effTier }
      : { engine: 'sora', duration: form.duration, model: effModel };
    api.post('/admin/video/estimate-cost', payload)
      .then(({ data }) => { if (!cancelled) setEstCostGbp(data.gbp ?? null); })
      .catch(() => { if (!cancelled) setEstCostGbp(null); });
    return () => { cancelled = true; };
  }, [form.engine, form.duration, form.model, form.seedanceTier, autoRouteHint]);

  // Live auto-route preview: while engine='auto' and brief is non-trivial,
  // call the auto-router so the user can see which engine WILL be picked.
  useEffect(() => {
    if (form.engine !== 'auto') { setAutoRouteHint(null); return; }
    if (!form.brief.trim() || form.brief.trim().length < 12) { setAutoRouteHint(null); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post('/admin/video/auto-route', {
        brief: form.brief, duration: form.duration, aspect: form.aspect,
      }).then(({ data }) => { if (!cancelled) setAutoRouteHint(data); })
        .catch(() => { if (!cancelled) setAutoRouteHint(null); });
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.engine, form.brief, form.duration, form.aspect]);

  if (!user) return null;
  if (!user.isAdmin) {
    return (
      <div className="min-h-screen bg-[#fdfaf6]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 py-20 text-center">
          <Crown className="w-12 h-12 mx-auto text-slate-300" />
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Admin only</h1>
          <p className="mt-1 text-slate-500">Video generation is admin-only while we trial it.</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-6">Back to dashboard</Button>
        </div>
      </div>
    );
  }

  // ---- Step 1: Review prompt -------------------------------------------- //
  const reviewPrompt = async () => {
    if (!form.brief.trim()) {
      toast({ title: 'Brief is required', description: 'Tell the AI what to advertise first.' });
      return;
    }
    setReviewing(true);
    try {
      const { data } = await api.post('/admin/video/rewrite-prompt', {
        businessName: form.businessName,
        businessType: form.businessType,
        tone: form.tone,
        brief: form.brief,
      });
      setScene(data.visualScene || '');
      toast({
        title: 'AI scene drafted',
        description: 'Review, edit if you wish, then click Approve & Generate.',
      });
    } catch (e) {
      toast({ title: 'Could not draft scene', description: e?.response?.data?.detail || e.message });
    } finally {
      setReviewing(false);
    }
  };

  // ---- Custom logo upload ----------------------------------------------- //
  const onLogoFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/video/upload-logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, logoSource: 'custom', logoId: data.logoId, customLogoName: file.name }));
      toast({ title: 'Logo uploaded', description: file.name });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message });
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---- Step 2: Approve scene & generate --------------------------------- //
  const approveAndGenerate = async () => {
    if (!scene.trim()) {
      toast({ title: 'Scene is empty', description: 'Add a description or click Review prompt again.' });
      return;
    }
    if (form.logoSource === 'custom' && !form.logoId) {
      toast({ title: 'No logo uploaded', description: 'Upload a logo or pick a different option.' });
      return;
    }
    let safeForm = { ...form };
    if (safeForm.aspect === 'reel' && safeForm.model === 'sora-2') {
      safeForm.model = 'sora-2-pro';
      setForm(safeForm);
      toast({ title: 'Switched to Sora 2 Pro', description: 'Portrait 9:16 is only available on Sora 2 Pro.' });
    }
    try {
      const payload = {
        businessName: safeForm.businessName,
        businessType: safeForm.businessType,
        tone: safeForm.tone,
        brief: safeForm.brief,
        aspect: safeForm.aspect,
        duration: safeForm.duration,
        model: safeForm.model,
        withReferenceImage: safeForm.withReferenceImage,
        visualScene: scene,
        logoSource: safeForm.logoSource,
        logoId: safeForm.logoSource === 'custom' ? safeForm.logoId : null,
        engine: safeForm.engine,
        seedanceTier: safeForm.seedanceTier,
        seedanceResolution: safeForm.seedanceResolution,
      };
      const { data } = await api.post('/admin/video/generate', payload);
      setJob({ id: data.jobId, status: 'queued', stage: 'queued' });
      toast({ title: 'Video job started', description: 'Sora is generating — usually 2–5 minutes.' });
    } catch (e) {
      toast({ title: 'Generation failed to start', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  const resetAll = () => {
    setJob(null);
    setVideoUrl(null);
    setRefUrl(null);
    setScene('');
    setExtendPrompt('');
    setExtendJob(null);
  };

  // ---- Extend an existing video ----------------------------------------- //
  const submitExtend = async () => {
    if (!job?.id) return;
    if (!extendPrompt.trim()) {
      toast({ title: 'Continuation prompt required', description: 'Describe what should happen next on screen.' });
      return;
    }
    try {
      const { data } = await api.post('/admin/video/extend', {
        parentJobId: job.id,
        prompt: extendPrompt,
        duration: extendDuration,
        logoSource: form.logoSource,
        logoId: form.logoSource === 'custom' ? form.logoId : null,
      });
      setExtendJob({ id: data.jobId, status: 'queued', stage: 'queued' });
      toast({ title: 'Extension started', description: 'Sora is extending — usually 1–3 minutes.' });
    } catch (e) {
      toast({ title: 'Extension failed to start', description: e?.response?.data?.detail || 'Try again.' });
    }
  };

  const toggleSpeedMode = (on) => {
    if (on) {
      // Lock to the cheapest, fastest preset — overrides any prior selections.
      setForm(f => ({
        ...f,
        speedMode: true,
        engine: 'seedance',
        seedanceTier: 'fast',
        seedanceResolution: '480p',
        withReferenceImage: false,
        // Cap to Seedance single-shot ceiling (15s)
        duration: f.duration > 15 ? 8 : f.duration,
      }));
    } else {
      setForm(f => ({ ...f, speedMode: false }));
    }
  };

  const stage = STAGE_LABELS[job?.stage] || STAGE_LABELS.queued;
  const StageIcon = stage.icon;
  const isWorking = job && job.status !== 'done' && job.status !== 'failed';

  return (
    <div className="min-h-screen bg-[#fdfaf6]" data-testid="admin-video-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10">
        <Button onClick={() => navigate('/admin')} variant="ghost" size="sm" className="mb-3">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Admin dashboard
        </Button>

        <div className="flex items-start gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center text-white shadow-md">
            <Video className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">AI Video Ad Studio</h1>
            <p className="text-slate-600 text-sm">Sora 2 + Nano Banana · admin-only · 4 / 8 / 12 sec ads with native audio &amp; logo overlay</p>
          </div>
        </div>

        <div className="mt-3 mb-8 flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Internal preview.</strong> Each generation costs ~£0.30–0.80 of Emergent LLM key balance and takes 2–5 minutes.
            Test thoroughly before we expose this to paying customers.
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: form + reviewer */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Business name</label>
              <Input
                value={form.businessName}
                onChange={e => setForm(f => ({ ...f, businessName: e.target.value }))}
                placeholder="Bloom & Petal Florists"
                data-testid="video-business-name"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Business type</label>
              <Input
                value={form.businessType}
                onChange={e => setForm(f => ({ ...f, businessType: e.target.value }))}
                placeholder="Florist · Cafe · Salon · Tradesperson…"
                data-testid="video-business-type"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Brief</label>
              <Textarea
                value={form.brief}
                onChange={e => { setForm(f => ({ ...f, brief: e.target.value })); setScene(''); }}
                placeholder="Describe what should appear ON SCREEN — e.g. 'A smiling florist arranging vibrant pink and yellow bouquets in a bright UK shop, then a quick handover to a happy customer.'"
                data-testid="video-brief"
                className="mt-1 min-h-[110px]"
              />
              <p className="mt-1.5 text-xs text-slate-500 leading-snug">
                Tip: describe a <strong>visual scene</strong> (people, action, setting) — not marketing copy.
                Avoid third-party brand names, prices, slogans &amp; hashtags.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tone</label>
                <Select value={form.tone} onValueChange={v => setForm(f => ({ ...f, tone: v }))}>
                  <SelectTrigger className="mt-1" data-testid="video-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="energetic">Energetic</SelectItem>
                    <SelectItem value="reassuring">Reassuring</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Aspect ratio</label>
                <Select value={form.aspect} onValueChange={v => setForm(f => ({ ...f, aspect: v }))}>
                  <SelectTrigger className="mt-1" data-testid="video-aspect"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASPECTS.map(a => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Speed Mode — one-click "good enough fast & cheap" preset */}
            <button
              type="button"
              onClick={() => toggleSpeedMode(!form.speedMode)}
              data-testid="video-speed-mode"
              className={`w-full text-left rounded-xl border-2 px-4 py-3 transition flex items-start gap-3 ${
                form.speedMode
                  ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50 shadow-sm'
                  : 'border-slate-200 bg-white hover:border-amber-300'
              }`}
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                form.speedMode ? 'bg-amber-400 text-white' : 'bg-amber-100 text-amber-600'
              }`}>
                <Zap className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900 text-sm">⚡ Speed Mode</span>
                  {form.speedMode && (
                    <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded">ON</span>
                  )}
                </div>
                <div className="text-xs text-slate-600 leading-snug mt-0.5">
                  {form.speedMode
                    ? 'Locked to Seedance Fast · 480p · no reference still · ≤15s. Expect ~90 sec generation, ~£0.10–0.40 per video.'
                    : 'One click: Seedance Fast · 480p · ≤15s. About 90 sec to generate, ~£0.10–0.40. Tap for cheap & cheerful ads.'}
                </div>
              </div>
            </button>

            {/* Engine selector */}
            <div className={form.speedMode ? 'opacity-50 pointer-events-none' : ''}>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Engine{form.speedMode ? ' (locked by Speed Mode)' : ''}</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, engine: 'auto' }))}
                  data-testid="video-engine-auto"
                  className={`text-left rounded-lg border px-3 py-2 transition ${
                    form.engine === 'auto'
                      ? 'border-orange-500 bg-orange-50 text-orange-900'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="text-xs font-semibold">Auto · cheapest</div>
                  <div className="text-[11px] text-slate-500">We pick the best engine for you</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, engine: 'sora', duration: SORA_DURATIONS.some(d=>d.v===f.duration)?f.duration:8 }))}
                  data-testid="video-engine-sora"
                  className={`text-left rounded-lg border px-3 py-2 transition ${
                    form.engine === 'sora'
                      ? 'border-orange-500 bg-orange-50 text-orange-900'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="text-xs font-semibold">Sora 2 / Pro</div>
                  <div className="text-[11px] text-slate-500">People · 4–12s · extendable</div>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, engine: 'seedance' }))}
                  data-testid="video-engine-seedance"
                  className={`text-left rounded-lg border px-3 py-2 transition ${
                    form.engine === 'seedance'
                      ? 'border-orange-500 bg-orange-50 text-orange-900'
                      : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="text-xs font-semibold">Seedance 2.0</div>
                  <div className="text-[11px] text-slate-500">Products · 4–15s · cheaper</div>
                </button>
              </div>
              {form.engine === 'auto' && autoRouteHint?.reason && (
                <div className="mt-2 text-xs text-slate-700 bg-orange-50 border border-orange-200 rounded-md px-3 py-2 leading-snug" data-testid="video-auto-hint">
                  <span className="font-semibold text-orange-900">{autoRouteHint.engine === 'sora' ? 'Sora 2' : 'Seedance 2.0'}</span> — {autoRouteHint.reason.replace(/^Auto:\s*/, '')}
                  {Array.isArray(autoRouteHint.extensions) && autoRouteHint.extensions.length > 0 && (
                    <div className="mt-1.5 text-[11px] text-slate-600">
                      <strong>Plan:</strong> 12s base + {autoRouteHint.extensions.length} extension{autoRouteHint.extensions.length === 1 ? '' : 's'}
                      {' '}(<code>{autoRouteHint.extensions.map(e => `+${e}s`).join(' ')}</code>) → ~{autoRouteHint.finalDuration}s total.
                      Each extension is its own Sora call (~1–3 min &amp; cost), runs automatically.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</label>
                <Select value={String(form.duration)} onValueChange={v => setForm(f => ({ ...f, duration: parseInt(v) }))}>
                  <SelectTrigger className="mt-1" data-testid="video-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const list = form.speedMode
                        ? SEEDANCE_DURATIONS  // ≤15s only
                        : (form.engine === 'auto'
                            ? AUTO_DURATIONS
                            : (form.engine === 'sora' ? SORA_DURATIONS : SEEDANCE_DURATIONS));
                      return list.map(d =>
                        <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {form.engine === 'auto' ? 'Quality' : (form.engine === 'seedance' ? 'Tier' : 'Model')}
                </label>
                {form.engine === 'auto' ? (
                  <div className="mt-1 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm text-slate-600" data-testid="video-auto-quality">
                    Auto-picked
                  </div>
                ) : form.engine === 'sora' ? (
                  <Select value={form.model} onValueChange={v => setForm(f => ({ ...f, model: v }))}>
                    <SelectTrigger className="mt-1" data-testid="video-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sora-2-pro">Sora 2 Pro · highest quality</SelectItem>
                      <SelectItem value="sora-2">Sora 2 · faster · landscape only</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={form.seedanceTier} onValueChange={v => setForm(f => ({ ...f, seedanceTier: v }))}>
                    <SelectTrigger className="mt-1" data-testid="video-tier"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fast">Fast · ~20% cheaper</SelectItem>
                      <SelectItem value="standard">Standard · best quality</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Cost estimator */}
            {estCostGbp != null && (
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between" data-testid="video-cost-estimate">
                <span>Approx cost</span>
                <strong className="text-slate-900">£{estCostGbp.toFixed(2)}</strong>
              </div>
            )}

            <label className={`flex items-center justify-between pt-1 cursor-pointer ${form.speedMode ? 'opacity-50' : ''}`}>
              <div>
                <div className="text-sm font-medium text-slate-900">Generate Nano Banana reference still</div>
                <div className="text-xs text-slate-500">A separate ad image you can also post to Meta. (Sora itself doesn't yet accept reference images.){form.speedMode ? ' · Disabled in Speed Mode.' : ''}</div>
              </div>
              <Switch
                checked={form.withReferenceImage}
                onCheckedChange={v => setForm(f => ({ ...f, withReferenceImage: v }))}
                disabled={form.speedMode}
                data-testid="video-ref-toggle"
              />
            </label>

            {/* Logo overlay */}
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Logo overlay (bottom-right)</div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'default', label: 'MBAP logo', sub: 'Default brand mark' },
                  { id: 'custom',  label: 'Upload',     sub: form.customLogoName || 'PNG / JPG / WebP' },
                  { id: 'none',    label: 'No logo',    sub: 'Plain video' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setForm(f => ({ ...f, logoSource: opt.id }))}
                    data-testid={`video-logo-${opt.id}`}
                    className={`text-left rounded-lg border px-3 py-2 transition ${
                      form.logoSource === opt.id
                        ? 'border-orange-500 bg-orange-50 text-orange-900'
                        : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <div className="text-xs font-semibold">{opt.label}</div>
                    <div className="text-[11px] text-slate-500 truncate">{opt.sub}</div>
                  </button>
                ))}
              </div>
              {form.logoSource === 'custom' && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={onLogoFilePicked}
                    data-testid="video-logo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    data-testid="video-logo-upload-btn"
                  >
                    {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    {form.logoId ? 'Replace logo' : 'Choose file'}
                  </Button>
                  {form.logoId && (
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {form.customLogoName || 'logo uploaded'}
                    </span>
                  )}
                </div>
              )}
              <p className="mt-2 text-[11px] text-slate-500">Stamped at 10% width with ~85% opacity in the bottom-right corner.</p>
            </div>

            {/* Step 1: Review prompt */}
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Step 1 · Prompt reviewer</div>
                {scene && <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> AI scene drafted</span>}
              </div>
              <Button
                onClick={reviewPrompt}
                disabled={reviewing || isWorking || !form.brief.trim()}
                variant="outline"
                className="w-full"
                data-testid="video-review-btn"
              >
                {reviewing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Drafting scene…</>
                           : <><Pencil className="w-4 h-4 mr-2" /> {scene ? 'Re-draft scene' : 'Review prompt'}</>}
              </Button>

              {scene && (
                <div className="mt-3">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Edit the AI scene if you wish</label>
                  <Textarea
                    value={scene}
                    onChange={e => setScene(e.target.value)}
                    className="mt-1 min-h-[140px] text-sm"
                    data-testid="video-scene-editor"
                  />
                  <p className="mt-1 text-[11px] text-slate-500">
                    This is exactly what Sora will see. Keep it visual: actions, location, lighting, mood.
                  </p>
                </div>
              )}
            </div>

            {/* Step 2: Approve & generate */}
            <Button
              onClick={approveAndGenerate}
              disabled={!scene.trim() || isWorking}
              className="w-full bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-semibold disabled:opacity-50"
              data-testid="video-generate-btn"
            >
              {isWorking
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                : <><Sparkles className="w-4 h-4 mr-2" /> Step 2 · Approve &amp; generate video</>}
            </Button>
            {!scene.trim() && (
              <p className="text-[11px] text-slate-500 text-center">Click <strong>Review prompt</strong> first — every video must go through the reviewer.</p>
            )}
          </div>

          {/* Right: live preview */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 min-h-[420px] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Preview</h2>
              {isWorking && (
                <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Polling every 4s
                </span>
              )}
            </div>

            {!job && (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-xl py-12 px-6 text-center">
                <Video className="w-10 h-10 mb-3 opacity-40" />
                Fill in the brief, review the AI scene, then click <strong className="mx-1 text-slate-700">Approve &amp; generate</strong>.
              </div>
            )}

            {job && isWorking && (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                <StageIcon className="w-10 h-10 text-orange-500 mb-3" />
                <div className="text-slate-900 font-semibold">{stage.label}</div>
                {job.extensionProgress && job.stage === 'sora_extension' && (
                  <div className="mt-1 text-xs text-slate-500">Extension {job.extensionProgress}</div>
                )}
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-4 max-w-xs">
                  <div className="h-full bg-gradient-to-r from-orange-400 to-rose-500 rounded-full transition-all duration-500" style={{ width: `${stage.progress}%` }} />
                </div>
                <div className="text-xs text-slate-500 mt-3">Job <code>{job.id?.slice(0, 8)}…</code></div>
                {job.hasReferenceImage && refUrl && (
                  <img src={refUrl} alt="Reference still" className="mt-5 rounded-lg max-h-40 border border-slate-200" />
                )}
              </div>
            )}

            {job?.status === 'failed' && (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                <AlertTriangle className="w-10 h-10 text-rose-500 mb-3" />
                <div className="text-slate-900 font-semibold">Generation failed</div>
                <div className="text-xs text-rose-700 mt-1 max-w-md">{job.error || 'Unknown error'}</div>
                <Button onClick={approveAndGenerate} variant="outline" size="sm" className="mt-5" data-testid="video-retry-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
                </Button>
              </div>
            )}

            {job?.status === 'done' && (
              <div className="flex-1 space-y-3" data-testid="video-result">
                {videoUrl ? (
                  <video
                    src={videoUrl}
                    controls
                    autoPlay
                    loop
                    className="w-full rounded-xl border border-slate-200 bg-black"
                    style={{ aspectRatio: form.aspect === 'reel' ? '9/16' : '16/9', maxHeight: 480 }}
                  />
                ) : (
                  <div className="aspect-video flex items-center justify-center bg-slate-100 rounded-xl text-slate-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading video…
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button asChild disabled={!videoUrl} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="video-download-btn">
                    <a href={videoUrl || '#'} download={`mbap-ad-${job.id}.mp4`}>
                      <Download className="w-4 h-4 mr-1.5" /> Download MP4
                    </a>
                  </Button>
                  {job.hasReferenceImage && refUrl && (
                    <Button asChild variant="outline">
                      <a href={refUrl} download={`mbap-ref-${job.id}.png`}>
                        <ImageIcon className="w-4 h-4 mr-1.5" /> Reference still
                      </a>
                    </Button>
                  )}
                  <Button onClick={resetAll} variant="outline">
                    <Sparkles className="w-4 h-4 mr-1.5" /> New ad
                  </Button>
                </div>
                {job.logoApplied === false && job.logoError && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                    ⚠ Logo overlay skipped: {job.logoError}
                  </p>
                )}
                {job.visualScene && (
                  <details className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3 border border-slate-200">
                    <summary className="cursor-pointer font-semibold text-slate-700">Visual scene used</summary>
                    <p className="mt-2 leading-relaxed">{job.visualScene}</p>
                  </details>
                )}
                {job.fallbackUsed && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5 border border-amber-200">
                    ⚠ Fell back to <strong>sora-2-pro</strong> because Sora 2 base rejected the request: {job.fallbackReason}
                  </p>
                )}

                {/* Feedback widget — appears once per finished job */}
                <FeedbackWidget mediaType="video" mediaId={job.id} />

                {/* Extend video panel */}
                {(() => {
                  const totalSecs = job.totalSeconds || job.duration || 0;
                  const used = job.extensionsUsed || 0;
                  const remainingSecs = MAX_TOTAL_SECONDS - totalSecs;
                  const canExtend = !!job.openaiVideoId && used < MAX_EXTENSIONS && remainingSecs >= 4;
                  const extending = extendJob && extendJob.status !== 'done' && extendJob.status !== 'failed';
                  const extStage = STAGE_LABELS[extendJob?.stage] || STAGE_LABELS.queued;
                  const ExtIcon = extStage.icon;
                  return (
                    <div className="rounded-xl border-2 border-dashed border-orange-200 bg-orange-50/40 p-4" data-testid="video-extend-panel">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                            <Plus className="w-4 h-4 text-orange-500" /> Make this video longer
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Now: <strong>{totalSecs}s</strong> · {used}/{MAX_EXTENSIONS} extensions used · up to {MAX_TOTAL_SECONDS}s total
                          </div>
                        </div>
                      </div>
                      {!job.openaiVideoId && (
                        <p className="text-xs text-amber-700 bg-amber-100 rounded-md p-2 border border-amber-200">
                          This video was generated before extension support was added — generate a new one to enable extensions.
                        </p>
                      )}
                      {job.openaiVideoId && used >= MAX_EXTENSIONS && (
                        <p className="text-xs text-slate-600 bg-slate-100 rounded-md p-2 border border-slate-200">
                          Maximum extensions reached for this video.
                        </p>
                      )}
                      {canExtend && !extending && (
                        <div className="space-y-2">
                          <Textarea
                            value={extendPrompt}
                            onChange={e => setExtendPrompt(e.target.value)}
                            placeholder="What happens next on screen? e.g. 'The customer smiles and walks out into the busy high street holding the bouquet.'"
                            className="min-h-[80px] text-sm"
                            data-testid="video-extend-prompt"
                          />
                          <div className="flex flex-wrap gap-2">
                            {EXTEND_DURATIONS.map(d => {
                              const tooLong = totalSecs + d.v > MAX_TOTAL_SECONDS;
                              return (
                                <button
                                  key={d.v}
                                  onClick={() => setExtendDuration(d.v)}
                                  disabled={tooLong}
                                  data-testid={`video-extend-dur-${d.v}`}
                                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition disabled:opacity-30 disabled:cursor-not-allowed ${
                                    extendDuration === d.v
                                      ? 'bg-orange-500 border-orange-500 text-white'
                                      : 'bg-white border-slate-300 text-slate-700 hover:border-orange-400'
                                  }`}
                                >
                                  {d.label}
                                </button>
                              );
                            })}
                          </div>
                          <Button
                            onClick={submitExtend}
                            disabled={!extendPrompt.trim()}
                            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                            data-testid="video-extend-submit"
                          >
                            <Plus className="w-4 h-4 mr-1.5" /> Extend by +{extendDuration}s
                          </Button>
                        </div>
                      )}
                      {extending && (
                        <div className="flex items-center gap-2 text-sm text-slate-700 py-2">
                          <ExtIcon className="w-4 h-4 text-orange-500 animate-pulse" />
                          {extStage.label}
                        </div>
                      )}
                      {extendJob?.status === 'failed' && (
                        <p className="text-xs text-rose-700 bg-rose-50 rounded-md p-2 border border-rose-200">
                          ⚠ {extendJob.error || 'Extension failed'}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Recover panel — pull videos directly from OpenAI when something
            disappears from our queue (e.g., backend restart) */}
        <div className="mt-10 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="recover-panel">
          <button
            onClick={() => { setRecoverOpen(o => !o); if (!recoverOpen && recoverItems.length === 0) loadRecover(); }}
            className="w-full px-5 py-3 border-b border-slate-100 flex items-center justify-between hover:bg-slate-50"
            data-testid="recover-toggle"
          >
            <div className="flex items-center gap-2">
              <LifeBuoy className="w-4 h-4 text-orange-500" />
              <h3 className="font-bold text-slate-900 text-sm">Recover lost videos from OpenAI</h3>
            </div>
            <span className="text-xs text-slate-500">{recoverOpen ? 'Hide' : 'Show'}</span>
          </button>
          {recoverOpen && (
            <div className="p-5 space-y-3">
              <div className="text-xs text-slate-600 leading-relaxed">
                If a generation disappeared from the list (e.g., after a redeploy), the MP4 may still be on your OpenAI account for ~24 hours.
                Click <strong>Re-download</strong> below to pull it back, re-apply your logo, and save the MP4 to your device.
              </div>
              <Button onClick={loadRecover} disabled={recoverLoading} variant="outline" size="sm" data-testid="recover-refresh">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${recoverLoading ? 'animate-spin' : ''}`} />
                {recoverLoading ? 'Loading…' : 'Refresh from OpenAI'}
              </Button>
              {recoverItems.length === 0 && !recoverLoading && (
                <div className="text-xs text-slate-500">No recent videos found on OpenAI.</div>
              )}
              <div className="space-y-2">
                {recoverItems.map(v => (
                  <div key={v.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-slate-700 truncate">{v.id.slice(0, 28)}…</div>
                      <div className="text-slate-500">
                        {v.status} · {v.model} · {v.seconds}s · {v.size} ·{' '}
                        {new Date(v.createdAt * 1000).toLocaleString('en-GB')}
                      </div>
                    </div>
                    {v.downloadable ? (
                      <Button
                        size="sm"
                        onClick={() => downloadRecovered(v.id)}
                        className="bg-orange-500 hover:bg-orange-400 text-white ml-3"
                        data-testid={`recover-download-${v.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Re-download
                      </Button>
                    ) : (
                      <span className="text-amber-700 ml-3 inline-flex items-center gap-1">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> {v.status}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* History */}
        {history.length > 0 && (
          <div className="mt-10 bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Recent generations</h3>
              <Button onClick={refreshHistory} variant="ghost" size="sm">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">When</th>
                    <th className="text-left px-4 py-2.5">Brief</th>
                    <th className="text-left px-4 py-2.5">Spec</th>
                    <th className="text-left px-4 py-2.5">Status</th>
                    <th className="text-left px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.map(j => (
                    <tr key={j.id}>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{(j.createdAt || '').slice(11, 19)}</td>
                      <td className="px-4 py-2.5 text-slate-700 max-w-[300px] truncate" title={j.brief}>{j.brief}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{j.aspect} · {j.duration}s · {j.model}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          j.status === 'done' ? 'bg-emerald-100 text-emerald-700' :
                          j.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{j.status}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        {j.hasVideo && (
                          <button
                            onClick={() => openHistoryItem(j)}
                            className="text-orange-600 text-xs font-medium hover:underline"
                            data-testid={`open-video-${j.id}`}
                          >
                            Open
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminVideo;
